/**
 * Preprocess markdown so single * and single ~ are treated as bold and strikethrough
 * (WhatsApp-style). Runs before remark-parse so ** and ~~ still work.
 *
 * Why per-line: with `/g`, after matching `\n~foo~\n` the engine resumes at `~` on the
 * next line; `(^|\s)~` fails there (`^` is only string start), so lines like `~*x*~` were
 * never converted to ~~…~~. Processing each logical line makes `^` mean line start.
 *
 * Extra passes mirror nested WhatsApp combos so remark output matches markdown-it in RN.
 * Keep in sync with rely-rn `src/utils/remarkWhatsAppPreprocess.ts`.
 */

const SPLIT_LINES = /(\r\n|\n|\r)/;

/** Inline markdown link [label](destination); allows escaped chars inside label/URL. */
const MARKDOWN_LINK = /\[(?:[^\]\]\\]|\\.)*\]\((?:[^)\\]|\\.)*\)/;

/** Bold inner content: plain chars or asterisks inside markdown link destinations. */
const BOLD_INNER = `(?:[^*]|${MARKDOWN_LINK.source})+`;

const WHATSAPP_BOLD = new RegExp(`(^|\\s)\\*(${BOLD_INNER})\\*(\\s|$)`, 'g');
const ITALIC_OUTSIDE_BOLD = new RegExp(`_\\*(${BOLD_INNER})\\*_`, 'g');
const STRIKE_OUTSIDE_BOLD = new RegExp(`~~\\*(${BOLD_INNER})\\*~~`, 'g');

/**
 * (^|\s)\*([^*]+)\*(\s|$)  -> $1**$2**$3  (single-* bold)
 * (^|\s)~([^~]+)~(\s|$)    -> $1~~$2~~$3  (single-~ strikethrough)
 * ```([^`\n]+)```          -> `$1`        (triple-backtick inline code, for compatibility)
 */
function preprocessLine(line) {
  let out = line;

  // Italic outside, bold inside: _*text*_
  out = out.replace(ITALIC_OUTSIDE_BOLD, '_**$1**_');

  // Italic outside, strikethrough inside: _~text~_
  out = out.replace(/_~([^~]+)~_/g, '_~~$1~~_');

  out = out.replace(WHATSAPP_BOLD, '$1**$2**$3');
  out = out.replace(/(^|\s)~([^~]+)~(\s|$)/g, '$1~~$2~~$3');

  // Strikethrough outside, bold inside: ~~*text*~~
  out = out.replace(STRIKE_OUTSIDE_BOLD, '~~**$1**~~');

  return out;
}

export function preprocessWhatsAppMarkdown(md) {
  if (typeof md !== 'string') return String(md ?? '');
  // Fences without embedded newlines only (same as legacy regex).
  const withCode = md.replace(/```([^`\n]+)```/g, '`$1`');
  const parts = withCode.split(SPLIT_LINES);
  return parts.map((part, i) => (i % 2 === 1 ? part : preprocessLine(part))).join('');
}
